// createTypographyVariant.ts - Factory function for creating polymorphic typography variants
import React, { forwardRef } from "react";
import { wishUITheme } from "../theme/theme";
import { PolymorphicForwardRefComponent } from "./PolymorphicTypes";
import Typography from "../primitives/Typography/Typography";
import { TypographyBaseProps } from "../primitives/Typography/Typography.types";

// Define valid HTML element types for typography
type TypographyElementType =
  | "p"
  | "span"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "label"
  | "div";

// Fallback weight mapping in case the theme doesn't provide it
const fallbackFontWeightMap = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
};

// Type for a typography variant component
export type TypographyVariantComponent<
  DefaultElement extends React.ElementType = "p"
> = PolymorphicForwardRefComponent<DefaultElement, TypographyBaseProps>;

// Export variant base props for easier extension
export type VariantBaseProps = {
  fontFamily?: "primary" | "fallback" | string;
};

/**
 * Creates a typography variant component based on the design system theme
 *
 * @param fontVariantType - The type of typography (body, heading, caps, serif-heading)
 * @param styleVariationNumber - The font style variation number (1-7 depending on type)
 * @param fontWeight - The font weight (regular, medium, semiBold, bold, extraBold)
 * @param options - Additional options for customizing the variant
 * @returns A polymorphic React component with the specified typography styling
 */
export function createTypographyVariant(
  fontVariantType: string,
  styleVariationNumber: number,
  fontWeight: string,
  options: {
    fontFamily?: "primary" | "fallback" | string;
    textAlign?: "left" | "center" | "right" | "justify";
    as?: React.ElementType;
    displayName?: string;
  } = {}
): TypographyVariantComponent {
  // Determine default element type based on fontType and fontSize
  let defaultElement = options.as || "span";
  if (!options.as && fontVariantType === "heading") {
    // Map heading sizes to appropriate h1-h6 elements
    const headingMap: Record<number, TypographyElementType> = {
      0: "h1",
      1: "h1",
      2: "h2",
      3: "h3",
      4: "h4",
      5: "h5",
      6: "h6",
    };
    defaultElement = headingMap[styleVariationNumber];
  }

  // Create the component that will use the theme
  const TypographyVariant = forwardRef(function TypographyVariant(
    props: TypographyBaseProps & { as?: React.ElementType },
    ref
  ) {
    // Get the style from the theme's typography variants
    const style =
      wishUITheme.typography?.variants?.[fontVariantType]?.[
        styleVariationNumber
      ];

    // Use theme values or fallbacks
    const size = style?.fontSize || 16;
    const lineHeight = style?.lineHeight || 24;
    const letterSpacing = style?.letterSpacing;

    // Get font weights from theme or use fallback
    const fontWeightValue =
      wishUITheme.typography?.fontWeights?.[fontWeight] ||
      fallbackFontWeightMap[fontWeight as keyof typeof fallbackFontWeightMap] ||
      400;

    // Simply pass the fontFamily key/name to Typography component
    // Typography component will handle the proper resolution with fallbacks
    const fontFamily = options.fontFamily || "primary";

    // Determine text alignment (default to left)
    const textAlign = options.textAlign || "left";

    // Determine if text should be uppercase (for caps type)
    const textTransform = fontVariantType === "caps" ? "uppercase" : undefined;

    const { as = defaultElement, ...rest } = props;

    return (
      <Typography
        ref={ref}
        as={as}
        fontFamily={fontFamily}
        fontSize={size}
        lineHeight={lineHeight}
        letterSpacing={letterSpacing}
        fontWeight={fontWeightValue}
        textAlign={textAlign}
        textTransform={textTransform}
        {...rest}
      />
    );
  }) as TypographyVariantComponent;

  // Set display name if provided, or generate a default one
  TypographyVariant.displayName =
    options.displayName ||
    `${
      fontVariantType.charAt(0).toUpperCase() + fontVariantType.slice(1)
    }${styleVariationNumber}${
      fontWeight.charAt(0).toUpperCase() + fontWeight.slice(1)
    }Typography`;

  return TypographyVariant;
}

export default createTypographyVariant;
